import {FOOTPRINT_SHEET, UNIT_FOOTPRINTS} from '../configs/unitFootprintsAnimation';
import {FOOTPRINT_FRAME_BOUNDS} from '../configs/unitFootprintsFrameBounds';
import {
  buildUnitFootprints,
  resolveFootprintItemFrame,
  resolveWalkedDistance,
} from '../utils/effects/buildUnitFootprints';

// Событие сцены Phaser «кадр»: приходит со временем кадра
const FRAME_EVENT = 'update';

// Запас на сравнении дробных длин пути
const WALKED_EPSILON = 0.5;

// Снизу вверх: подпалина, трещины и полоса на земле, затем свечение и сам отпечаток,
// сверху вспышки, искры и знаки фракций
const FOOTPRINT_KIND_LAYERS = {
  scorch: 'ground',
  crack: 'ground',
  ribbon: 'ground',
  burn: 'prints',
  print: 'prints',
};
// Свет сложением идет отдельным слоем: на земле над подпалинами и трещинами, а в воздухе
// под искрами, волнами и частицами, иначе вспышка на приходе закрывает их собой
const ADD_LAYER_DEPTH_SHIFT = {ground: 0.001, air: -0.001};

const getTint = (color, alpha) => (((((alpha * 255) | 0) & 0xff) << 24) | color) >>> 0;

// Куски кадра в координатах текстуры и точках кадра от его центра: считаем один раз на кадр
const resolveQuadFrame = (src, index) => {
  const frame = src.texture.get(index);
  const [left, top, right, bottom] = FOOTPRINT_FRAME_BOUNDS[index];
  const {frameSize} = FOOTPRINT_SHEET;
  const half = frameSize / 2;
  return {
    glTexture: frame.glTexture,
    u0: frame.u0 + (frame.u1 - frame.u0) * (left / frameSize),
    u1: frame.u0 + (frame.u1 - frame.u0) * (right / frameSize),
    v0: frame.v0 + (frame.v1 - frame.v0) * (top / frameSize),
    v1: frame.v0 + (frame.v1 - frame.v0) * (bottom / frameSize),
    left: left - half,
    top: top - half,
    right: right - half,
    bottom: bottom - half,
    // Дальняя от центра точка рамки: по ней отсекаем деталь за краем экрана
    reach: Math.hypot(Math.max(-(left - half), right - half), Math.max(-(top - half), bottom - half)),
  };
};

/**
 * Отрисовка слоя следов: все детали слоя уходят одним пакетом и рисуют только рамку своего
 * содержимого. Отдельные спрайты на это не годятся: детали со сложением и без него идут
 * вперемешку, а каждая смена смешивания сбрасывает пакет. Один след сильного отряда давал
 * 340 вызовов отрисовки на кадр при 9 у всего поля, и на телефоне это резало частоту кадров.
 *
 * Сигнатура и порядок вызовов те же, что у batchSprite. Вызывает Phaser, this — не нужен
 */
export const renderFootprintLayer = (renderer, src, camera) => {
  const {quads} = src;
  camera.addToRenderList(src);
  const pipeline = renderer.pipelines.set(src.pipeline, src);
  let unit = pipeline.setGameObject(src);
  const {a, b, c, d, e, f} = camera.matrix;
  const {scrollX, scrollY, worldView: view} = camera;
  const quadFrames = src.quadFrames;

  pipeline.manager.preBatch(src);
  for (let index = 0; index < quads.length; index += 1) {
    const quad = quads[index];
    const quadFrame = quadFrames[quad.frame] ?? (quadFrames[quad.frame] = resolveQuadFrame(src, quad.frame));
    const {x, y, scaleX, scaleY} = quad;
    const reach = quadFrame.reach * Math.max(scaleX, scaleY);
    // Деталь за краем экрана: на телефоне поле видно лишь частью
    if (x + reach >= view.x && x - reach <= view.right && y + reach >= view.y && y - reach <= view.bottom) {
      if (pipeline.shouldFlush(6)) {
        pipeline.flush();
        unit = pipeline.setGameObject(src);
      }
      const cos = Math.cos(quad.rotation);
      const sin = Math.sin(quad.rotation);
      // Отражение — зеркало места, а не картинки: рамка и кадр остаются парой
      const xScale = quad.flipX ? -scaleX : scaleX;
      const leftX = quadFrame.left * xScale;
      const rightX = quadFrame.right * xScale;
      const topY = quadFrame.top * scaleY;
      const bottomY = quadFrame.bottom * scaleY;
      const originX = x - scrollX;
      const originY = y - scrollY;
      const x0 = originX + leftX * cos - topY * sin;
      const y0 = originY + leftX * sin + topY * cos;
      const x1 = originX + leftX * cos - bottomY * sin;
      const y1 = originY + leftX * sin + bottomY * cos;
      const x2 = originX + rightX * cos - bottomY * sin;
      const y2 = originY + rightX * sin + bottomY * cos;
      const x3 = originX + rightX * cos - topY * sin;
      const y3 = originY + rightX * sin + topY * cos;
      const tint = getTint(quad.tint, camera.alpha * quad.alpha);
      pipeline.batchQuad(
        src,
        x0 * a + y0 * c + e, x0 * b + y0 * d + f,
        x1 * a + y1 * c + e, x1 * b + y1 * d + f,
        x2 * a + y2 * c + e, x2 * b + y2 * d + f,
        x3 * a + y3 * c + e, x3 * b + y3 * d + f,
        quadFrame.u0, quadFrame.v0, quadFrame.u1, quadFrame.v1,
        tint, tint, tint, tint,
        0, quadFrame.glTexture, unit,
      );
    }
  }
  pipeline.manager.postBatch(src);
};

/**
 * СЛЕДЫ ЗА ИДУЩИМИ ОТРЯДАМИ (см. configs/unitFootprintsAnimation): что и где лежит,
 * решает чистое описание следа (utils/effects/buildUnitFootprints), а здесь — его
 * угасание и отрисовка.
 *
 * ЧАСЫ СЛЕДА — САМ ОТРЯД: каждый кадр смотрим, докуда дошел его спрайт, и проявляем
 * все, что он прошел. Поэтому след не обгоняет отряд и сам сжимается в записи матча.
 * Спрайт уничтожен (отряд дошел и поле перерисовалось) или рендерер сообщил о конце
 * пути (finish) — проступает остаток, и след начинает гаснуть.
 *
 * ДЕТАЛИ — НЕ СПРАЙТЫ: на сцене от следов не больше пяти объектов, по одному на слой и
 * смешивание, сколько бы следов ни шло сразу (см. renderFootprintLayer). Цена следа на
 * кадр не зависит от их числа в вызовах отрисовки, а появление и уход детали не трогают
 * список отображения и его сортировку.
 *
 * Слои принадлежат сцене, а не ячейкам: перерисовка поля их не трогает. След —
 * украшение: его сбой не роняет ход, а отключает следы до конца сцены.
 */
export default class UnitFootprints {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {{ground: number, prints: number, air: number}} [options.depths]
   * @param {number} [options.addBlendMode] — режим сложения света для волн и полос
   * @param {function(): number} [options.rng] — источник случайности (в тестах — заглушка)
   * @param {function({durationMs: number, intensity: number})} [options.onQuake] — шаг
   *   очень тяжелого отряда: дрожит камера
   */
  constructor(scene, {depths = {ground: 0, prints: 0, air: 0}, addBlendMode, rng = Math.random, onQuake} = {}) {
    this.scene = scene;
    this.depths = depths;
    this.addBlendMode = addBlendMode;
    this.onQuake = onQuake;
    this.rng = rng;
    this.trails = [];
    // Слои отрисовки по имени: заводятся, когда в них появится первая деталь
    this.layers = {};
    // Вид детали в кадре: один объект на все детали, чтобы кадр не оставлял мусора
    this.frameScratch = {};
    this.isListening = false;
    this.isFailed = false;
  }

  /**
   * Отряд пошел по пути
   *
   * @param {Object} params
   * @param {string} params.unitId
   * @param {Object} params.follow — спрайт идущего отряда
   * @param {Array<{x: number, y: number}>} params.points — путь от старта до цели
   * @param {number} params.hexSize
   * @param {string} params.unitTypeId
   * @param {number} params.unitSize
   * @param {string|null} params.factionId
   * @param {boolean} params.hasFactionSkin
   * @param {number} [params.timeScale] — сжатие срока жизни следа под скорость записи
   * @returns {boolean} появится ли след
   */
  start({unitId, follow, timeScale = 1, ...description}) {
    let isStarted = false;
    this.guard(() => {
      // Кадры в скрытой вкладке не идут: след завис бы и проступил на возврате
      if ((typeof document !== 'undefined' && document.hidden) || !this.scene.textures?.exists?.(FOOTPRINT_SHEET.key)) {
        return;
      }
      const model = buildUnitFootprints({...description, rng: this.rng});
      if (!model) {
        return;
      }

      // Новый ход того же отряда: прежний след дальше сам по себе
      this.finish(unitId);
      this.trails.push({
        unitId, follow, model, timeScale,
        walked: 0,
        nextItem: 0,
        finishedAtMs: null,
        cancelledAtMs: null,
        elapsedMs: 0,
        live: [],
      });
      // Лишние следы — самые старые — гаснут раньше срока
      this.trails.slice(0, Math.max(0, this.trails.length - UNIT_FOOTPRINTS.maxLiveTrails)).forEach((trail) => {
        this.cancelTrail(trail);
      });
      this.updateListening();
      isStarted = true;
    });
    return isStarted;
  }

  /**
   * Отряд дошел или его ход пропустили: остаток следа проступает разом
   * @param {string} unitId
   * @param {Object} [options]
   * @param {boolean} [options.isSkipped] — ход пропустили, отряд не шел: без толчков камеры
   */
  finish(unitId, {isSkipped = false} = {}) {
    this.guard(() => {
      this.trails.forEach((trail) => {
        if (trail.unitId === unitId) {
          trail.isSkipped = trail.isSkipped || (isSkipped && trail.finishedAtMs === null);
          this.finishTrail(trail);
        }
      });
    });
  }

  /**
   * Ход отменили (соперник взял его назад): след, который отряд еще вел, быстро гаснет.
   * Следы его прошлых ходов доживают свое
   * @param {string} unitId
   */
  cancel(unitId) {
    this.guard(() => {
      this.trails.forEach((trail) => {
        if (trail.unitId === unitId && trail.finishedAtMs === null) {
          this.cancelTrail(trail);
        }
      });
    });
  }

  finishTrail(trail) {
    if (trail.finishedAtMs === null) {
      trail.walked = trail.model.length;
      trail.finishedAtMs = trail.elapsedMs;
    }
  }

  cancelTrail(trail) {
    if (trail.cancelledAtMs === null) {
      trail.cancelledAtMs = trail.elapsedMs;
      if (trail.finishedAtMs === null) {
        trail.finishedAtMs = trail.elapsedMs;
      }
    }
  }

  /**
   * Доля угасания лежащей детали: фронт идет от начала пути к концу. Отмененный след
   * гаснет весь сразу
   */
  resolveFadeShare(trail, item) {
    if (trail.finishedAtMs === null) {
      return 0;
    }
    const {fadeSweepMs, print, aura} = UNIT_FOOTPRINTS;
    // Свет отряда гаснет сразу, как отряд дошел
    if (item.isFollow) {
      return (trail.elapsedMs - trail.finishedAtMs) / aura.fadeMs;
    }
    if (trail.cancelledAtMs !== null) {
      return (trail.elapsedMs - trail.cancelledAtMs) / print.fadeMs;
    }
    const startsAtMs = trail.finishedAtMs + trail.model.holdMs + fadeSweepMs * (item.atS / trail.model.length);
    return (trail.elapsedMs - startsAtMs) / print.fadeMs;
  }

  /** Шаг времени */
  update(delta) {
    this.guard(() => {
      const stepMs = Math.min(delta, UNIT_FOOTPRINTS.maxStepMs);
      if (!(stepMs > 0) || this.trails.length === 0) {
        return;
      }
      Object.values(this.layers).forEach((layer) => {
        layer.quads.length = 0;
      });
      this.trails = this.trails.filter((trail) => {
        trail.elapsedMs += stepMs / trail.timeScale;
        return this.drawTrail(trail);
      });
      // Пустой слой прячем: сцена сменила бы ради него смешивание и сбросила пакет
      Object.values(this.layers).forEach((layer) => {
        layer.setVisible(layer.quads.length > 0);
      });
      this.updateListening();
    });
  }

  advanceTrail(trail) {
    const {follow, model} = trail;
    if (trail.finishedAtMs !== null) {
      return;
    }
    if (!follow || follow.active === false) {
      this.finishTrail(trail);
      return;
    }
    trail.walked = resolveWalkedDistance(model.points, model.distances, trail.walked, {x: follow.x, y: follow.y});
    if (trail.walked >= model.length - WALKED_EPSILON) {
      this.finishTrail(trail);
    }
  }

  /**
   * Проявить пройденное и нарисовать живое
   * @returns {boolean} осталось ли в следе что показывать
   */
  drawTrail(trail) {
    this.advanceTrail(trail);
    const {model} = trail;

    // Отмененный след ничего нового не проявляет
    if (trail.cancelledAtMs === null) {
      while (trail.nextItem < model.items.length && model.items[trail.nextItem].atS <= trail.walked) {
        const item = model.items[trail.nextItem];
        if (item.kind === 'quake') {
          // Это не спрайт, а толчок. Камера дрожит только пока отряд идет: след, проступивший
          // разом после пропуска анимации, поле не трясет
          if (!trail.isSkipped) {
            this.onQuake?.({durationMs: item.durationMs, intensity: item.intensity});
          }
        } else {
          // Миллисекунда форы: с нулевым возрастом деталь невидима и опоздала бы на кадр
          trail.live.push({item, bornAtMs: trail.elapsedMs - 1, frame: item.frame, flipX: Boolean(item.flipX)});
        }
        trail.nextItem += 1;
      }
    } else {
      trail.nextItem = model.items.length;
    }

    // Сдвигаем живые на месте: новый массив на каждый след в каждом кадре — лишний мусор
    let liveCount = 0;
    for (let index = 0; index < trail.live.length; index += 1) {
      const entry = trail.live[index];
      const frame = resolveFootprintItemFrame(
        entry.item, trail.elapsedMs - entry.bornAtMs, this.resolveFadeShare(trail, entry.item), this.frameScratch
      );
      if (!frame.isDone) {
        trail.live[liveCount] = entry;
        liveCount += 1;
        if (frame.alpha > 0) {
          this.placeQuad(trail, entry, frame);
        }
      }
    }
    trail.live.length = liveCount;

    return liveCount > 0 || trail.nextItem < model.items.length;
  }

  /** Деталь попадает в свой слой: в этом кадре ее нарисуют такой */
  placeQuad(trail, entry, frame) {
    const {item} = entry;
    const scale = frame.size / FOOTPRINT_SHEET.frameSize;
    if (item.isFollow) {
      // Свет идет с отрядом, а когда спрайта уже нет, досвечивает на месте прихода
      const {points} = trail.model;
      const position = trail.follow?.active === false ? points[points.length - 1] : trail.follow;
      entry.x = position.x;
      entry.y = position.y;
    } else {
      entry.x = frame.x;
      entry.y = frame.y;
    }
    entry.rotation = frame.rotation;
    entry.alpha = Math.min(1, frame.alpha);
    entry.tint = frame.tint;
    entry.scaleX = scale * (frame.scaleX ?? 1);
    entry.scaleY = scale * (item.stretch ?? 1) * (frame.scaleY ?? 1);
    this.resolveLayer(item).quads.push(entry);
  }

  resolveLayer(item) {
    const level = FOOTPRINT_KIND_LAYERS[item.kind] ?? 'air';
    const isAdd = Boolean(item.isAdd) && this.addBlendMode !== undefined;
    const name = isAdd ? `${level}Add` : level;
    const known = this.layers[name];
    if (known) {
      return known;
    }
    const layer = this.scene.add.image(0, 0, FOOTPRINT_SHEET.key, 0);
    layer.quads = [];
    layer.quadFrames = [];
    layer.renderWebGL = renderFootprintLayer;
    layer.setDepth(this.depths[level] + (isAdd ? ADD_LAYER_DEPTH_SHIFT[level] : 0));
    if (isAdd) {
      layer.setBlendMode(this.addBlendMode);
    }
    this.layers[name] = layer;
    return layer;
  }

  /** Убрать все следы сразу: поле разбирается */
  clear() {
    const layers = Object.values(this.layers);
    this.trails = [];
    this.layers = {};
    layers.forEach((layer) => {
      try {
        layer.destroy();
      } catch (error) {
        // Сцена уже разбирается: слой мог быть уничтожен раньше нас
      }
    });
    this.updateListening();
  }

  /** Кадры слушаем, только пока есть живые следы */
  updateListening() {
    const events = this.scene?.events;
    const shouldListen = !this.isFailed && this.trails.length > 0;
    if (shouldListen && !this.isListening && events?.on) {
      events.on(FRAME_EVENT, this.handleFrame, this);
      this.isListening = true;
    } else if (!shouldListen && this.isListening) {
      events?.off?.(FRAME_EVENT, this.handleFrame, this);
      this.isListening = false;
    }
  }

  handleFrame(time, delta) {
    this.update(delta);
  }

  guard(action) {
    if (this.isFailed) {
      return;
    }
    try {
      action();
    } catch (error) {
      console.warn('[UnitFootprints] Следы за отрядами отключены: сбой', error);
      this.isFailed = true;
      this.clear();
    }
  }
}
